import React, { PureComponent } from "react";
import styled from "styled-components";
import { Loader } from "react-loaders";

import { getRequest, postRequest, downloadRequest } from "../../api/ApiService";
import { isSurveyFinished } from "../../api/Surveys";
import Auth from "../../routes/Auth";
import EventEmitter from "../../services/event-emitter";

const DOWNLOAD_MAX_ATTEMPTS = 10;
const DOWNLOAD_ATTEMPT_DELAY = 1000;
const DOWNLOAD_ERROR = "The system cannot process request. Try again later.";

const Wrapper = styled.div`
  width: 100%;
  padding: 40px 40px 50px;

  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin: 0 0 4px;
`;

const PageTitle = styled.h1`
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  font-family: 'Mukta Vaani';
  margin: 0;
`;

const DownloadLink = styled.a`
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #5C91F5;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  ${p => p.disabled && `
    color: rgba(0, 27, 59, 0.38);
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const PageSubtitle = styled.div`
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: rgba(0, 27, 59, 0.6);
  margin-bottom: 20px;
`;

const TableWrapper = styled.div`
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  font-family: 'Mukta Vaani';
  font-size: 14px;
  border-top: 1px solid #DEE0E3;
  border-collapse: collapse;
`;

const HeadGroupRow = styled.tr`
  background: #F5F6F7;
  box-shadow: inset 0px -1px 0px #DEE0E3;
`;

const HeadCell = styled.th`
  padding: 14px 16px 4px;
  font-weight: 700;
  text-align: ${p => (p.center ? "center" : "left")};
  vertical-align: top;
  white-space: nowrap;
  color: rgba(0, 27, 59, 0.94);
`;

const SubHeadRow = styled.tr`
  background: #F5F6F7;
  box-shadow: inset 0px -1px 0px #DEE0E3;
`;

const SubHeadCell = styled.th`
  padding: 0 16px 12px;
  font-weight: 500;
  font-size: 12px;
  text-align: ${p => (p.center ? "center" : "left")};
  color: rgba(0, 27, 59, 0.6);
  white-space: nowrap;
`;

const Row = styled.tr`
  box-shadow: inset 0px -1px 0px #ECEDEF;

  &:nth-child(2n-1) {
    background: #FAFBFD;
  }
`;

const Cell = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  white-space: nowrap;
  text-align: ${p => (p.center ? "center" : "left")};
`;

const EmptyRow = styled.tr`
  box-shadow: inset 0px -1px 0px #ECEDEF;
`;

const EmptyCell = styled.td`
  padding: 24px 16px;
  text-align: center;
  color: rgba(0, 27, 59, 0.6);
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;

  ${p => p.tone === 'yes' && `
    background: #E8F5EE;
    color: #2E7D4F;
  `}

  ${p => p.tone === 'no' && `
    background: #FBECEB;
    color: #B5413B;
  `}

  ${p => p.tone === 'na' && `
    background: #F0F1F4;
    color: rgba(0, 27, 59, 0.6);
  `}
`;

const formatPct = v =>
  (v == null || isNaN(Number(v))) ? "—" : `${(Number(v) * 100).toFixed(2)}%`;

const formatWeightPct = v =>
  (v == null || isNaN(Number(v))) ? "" : `${(Number(v) * 100).toFixed(2)}%`;

class FinalScores extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      weights: { team_grade: 0, peer_weight: 0 },
      roundsScores: {},
      peerScores: {},
      individualScores: {},
      latestClosedSurvey: null,
      // True after we've kicked off /usergrade/run/ in this session.
      // Prevents repeat POSTs while the backend job is processing —
      // the job is async (5-10 min) so we don't poll; instructor
      // refreshes the page when they return.
      individualScoresProcessing: false,
      // CSV download flow state, mirroring the pattern used by the
      // instructor dashboard's downloadPerformanceData:
      //   1. POST /usergrade/run/  → ensure scores are processed
      //   2. POST /reports/performance-csv-add/ → enqueue job, returns a polling URL
      //   3. poll that URL until status === Completed → download_link
      //   4. downloadRequest(link) → blob → trigger browser save
      performanceDownloadRequest: null,
      performanceDownloadCount: 0,
      performanceDownloadFile: null,
      performanceDownloading: false,
    };
  }

  componentDidMount() {
    // When the instructor lands on Final Scores after being on the
    // dashboard, App.js's state still holds the dashboard-scoped
    // general-info response, which intentionally omits teamplayers.
    // That leaves this.props.teamplayers empty and the roster renders
    // "No students found." until a hard refresh. Emit reload-data to
    // make App.js refire general-info — the current URL isn't
    // /instructor/dashboard, so scope=dashboard won't be sent and the
    // full payload (including teamplayers) comes back. One-shot flag
    // avoids a loop on courses that genuinely have 0 teamplayers.
    if (
      (this.props.teamplayers || []).length === 0
      && !this._triggeredReload
    ) {
      this._triggeredReload = true;
      EventEmitter.getInstance().trigger("reload-data");
    }
    if ((this.props.rounds || []).length > 0) {
      this._hasFetched = true;
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    const prevLen = (prevProps.rounds || []).length;
    const nextLen = (this.props.rounds || []).length;
    if (!this._hasFetched && nextLen > 0) {
      this._hasFetched = true;
      this.fetchData();
    } else if (nextLen !== prevLen) {
      this.fetchData();
    }
  }

  fetchData = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const rounds = (this.props.rounds || [])
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number));

    if (rounds.length === 0) {
      this.setState({ loaded: true });
      return;
    }

    this.setState({ loaded: false });

    const teamgradingsUrls = rounds.map(
      r => `${apiUrl}v2/games/teamgradings/overall/${r.id}/`
    );

    // Derive course id from the current game (game.course) — this
    // mirrors how the instructor dashboard does it. Auth.getCourseId()
    // reads sessionStorage, which is only set on the SSO/temporary
    // user flow and may be stale or missing for instructors who
    // manage multiple courses. game.course is always the course of
    // the game currently in context.
    const game = this.props.game;
    const courseId = (game && game.course) || Auth.getCourseId();

    // /usergrade/course/<id>/ returns one row per user *per round*
    // (count can be ~3× the student count for a 3-round game).
    // Keying by user_id without filtering means an arbitrary round's
    // row wins. Pin to the last closed round so each user only has
    // one row in the response and the value we read is the final
    // adjusted/curved cumulative. Skip the call entirely if no
    // round has closed — by spec, nothing should be shown then.
    // Sort by number ascending before picking the latest — the BE
    // returns rounds in insertion / id order, not number order (e.g.
    // (2, 3, 1) after Activate Main Rounds), so the raw last element
    // can be the wrong round.
    const finishedRounds = rounds
      .filter(r => r.is_finished === true)
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number));
    const closedLastRound = finishedRounds[finishedRounds.length - 1] || null;
    const usergradeCourseUrl = (courseId && closedLastRound)
      ? `${apiUrl}v2/games/usergrade/course/${courseId}/?round_id=${closedLastRound.id}`
      : null;

    const requests = [
      // Scope by course. Without ?course= the BE returns a paginated
      // list across every course this instructor touches, and
      // results[0] is whichever row happens to sort first (often a
      // foreign course), which was surfacing the wrong Team / Peer
      // weights in the table header. Matches the fix on
      // overall-score.js.
      `${apiUrl}v2/games/gradingweights/?course=${courseId}`,
      `${apiUrl}v2/forms/surveys/?course=${Auth.getCourseId()}`,
      ...teamgradingsUrls,
    ];
    if (usergradeCourseUrl) requests.push(usergradeCourseUrl);
    const usergradeIdx = usergradeCourseUrl ? requests.length - 1 : -1;

    // batchRequest is Promise.all — a single 4xx (e.g. the
    // usergrade/course endpoint 404s before scores are calculated)
    // poisons the whole batch and we lose all data. Wrap each call
    // so failures degrade per-column instead of blanking the page.
    Promise.all(
      requests.map(url =>
        getRequest(url).catch(() => ({ body: null, _failed: true }))
      )
    )
      .then(res => {
        // Grading weights — `gradingweights` is paginated; pull
        // team_grade and peer_weight from the first row.
        const weightsBody = (res[0] && res[0].body) || {};
        const weightsRow = (weightsBody.results && weightsBody.results[0]) || {};
        const weights = {
          team_grade: Number(weightsRow.team_grade) || 0,
          peer_weight: Number(weightsRow.peer_weight) || 0,
        };

        // Latest closed survey = the finished survey tagged stage
        // "final" (or legacy "second"). The BE's survey ids are NOT
        // guaranteed to be in creation order — observed on course
        // 265 where the Initial survey has id 155 while the Final
        // has id 154 — so picking by highest id would return the
        // Initial. If there's no finished final survey the Peer
        // Score + Individual Score columns intentionally render as
        // dashes (see the usergrade gating below).
        const surveysBody = (res[1] && res[1].body) || {};
        const surveys = Array.isArray(surveysBody)
          ? surveysBody
          : (surveysBody.results || []);
        const finishedSurveys = surveys.filter(s => isSurveyFinished(s));
        const stageOf = s => String((s && (s.stage || s.stage_name)) || "").toLowerCase();
        const isFinalStage = s => {
          const st = stageOf(s);
          return st === "final" || st === "second";
        };
        const finalFinishedSurveys = finishedSurveys.filter(isFinalStage);
        const latestClosedSurvey = finalFinishedSurveys.length > 0
          ? finalFinishedSurveys.reduce(
              (a, b) => (Number(a.id) > Number(b.id) ? a : b),
            )
          : null;

        // Per-round team grading rows: map round_id → { team_id → { current, cumulative } }
        const roundsScores = {};
        rounds.forEach((r, i) => {
          const body = (res[2 + i] && res[2 + i].body) || {};
          const teamsArr = Array.isArray(body.teams) ? body.teams : [];
          const byTeam = {};
          teamsArr.forEach(t => {
            byTeam[String(t.team)] = {
              current: Number(t.current_weighted_value),
              cumulative: Number(t.weighted_value),
            };
          });
          roundsScores[r.id] = byTeam;
        });

        // Individual Scores from /usergrade/course/<course_id>/.
        // Field preference matches the existing per-user pattern
        // in components/profile/index.js: curved value wins when
        // present, fall back to the un-curved adjusted value.
        //
        // Gated on `latestClosedSurvey`: Individual Score only makes
        // sense once the final peer eval has closed. If no finished
        // "final"-stage survey exists, we intentionally drop the
        // usergrade response and leave individualScores empty so the
        // column renders as "—" for every user. The usergrade/run
        // kickoff below is skipped for the same reason.
        const individualScores = {};
        if (latestClosedSurvey && usergradeIdx >= 0) {
          const ugBody = (res[usergradeIdx] && res[usergradeIdx].body) || {};
          const ugRows = Array.isArray(ugBody)
            ? ugBody
            : (ugBody.results || []);
          ugRows.forEach(row => {
            const uid = row.user_id != null ? row.user_id : row.user;
            if (uid == null) return;
            const curved = Number(row.curved_adjusted_cumulative_value);
            const adjusted = Number(row.adjusted_cumulative_value);
            const value = (!isNaN(curved) && curved > 0)
              ? curved
              : (!isNaN(adjusted) && adjusted > 0 ? adjusted : null);
            individualScores[String(uid)] = value;
          });
        }

        const finishPeerThen = (peerScores) => {
          this.setState(
            {
              loaded: true,
              weights,
              roundsScores,
              peerScores,
              individualScores,
              latestClosedSurvey,
            },
            () => {
              // Only fire /usergrade/run/ when a finished final
              // survey exists. Without it we have nothing to compile
              // scores against yet, and firing it would surface the
              // "being calculated" banner for a run that will have no
              // meaningful output.
              if (latestClosedSurvey) {
                this.maybeTriggerUsergradeRun(rounds, individualScores);
              }
            }
          );
        };

        if (!latestClosedSurvey) {
          finishPeerThen({});
          return;
        }

        getRequest(`${apiUrl}v2/games/peer-score/${latestClosedSurvey.id}/`)
          .then(peerRes => {
            const peerBody = (peerRes && peerRes.body) || {};
            const peerRows = Array.isArray(peerBody)
              ? peerBody
              : (peerBody.results || []);
            const peerScores = {};
            peerRows.forEach(row => {
              const uid = row.user_id != null ? row.user_id : row.user;
              if (uid == null) return;
              peerScores[String(uid)] = {
                peer_score: row.peer_score,
                completion: row.completion,
              };
            });
            finishPeerThen(peerScores);
          })
          .catch(() => {
            finishPeerThen({});
          });
      })
      .catch(() => {
        this.setState({ loaded: true });
      });
  };

  // If the last round has closed but the course-level usergrade
  // endpoint returned nothing, kick off the backend calc job once.
  // The job is async (5-10 min like the insights flow) so we don't
  // poll — we surface a "calculating" note instead and the
  // instructor refreshes when they return.
  maybeTriggerUsergradeRun = (sortedRounds, individualScores) => {
    if (this._runTriggered) return;
    if (this.state.individualScoresProcessing) return;
    if (individualScores && Object.keys(individualScores).length > 0) return;

    const lastRound = sortedRounds[sortedRounds.length - 1];
    if (!lastRound || lastRound.is_finished !== true) return;

    const game = this.props.game;
    const gameId = game ? game.id : Auth.getGameId();
    if (!gameId) return;

    this._runTriggered = true;
    this.setState({ individualScoresProcessing: true });
    postRequest(`${process.env.REACT_APP_API_URL}v2/games/usergrade/run/`)
      .send({ game: gameId })
      .type("form")
      .then(() => {
        // No re-fetch — backend job is long-running. The note in
        // the subtitle tells the instructor to come back.
      })
      .catch(() => {
        this.setState({ individualScoresProcessing: false });
        this._runTriggered = false;
      });
  };

  // The "Team Score" column is the cumulative `weighted_value` from
  // the latest closed (is_finished) round; if no round has finished
  // yet, fall back to the latest round we do have data for.
  getLastClosedRound = (sortedRounds) => {
    const finished = sortedRounds.filter(r => r.is_finished === true);
    if (finished.length > 0) return finished[finished.length - 1];
    return sortedRounds[sortedRounds.length - 1] || null;
  };

  // Round the Download button targets. The sidebar's "Year" dropdown
  // writes ?round=N into the URL (see round-switcher.js — "Year" is
  // actually the round number). Match that round when it exists in
  // the current game's rounds list; otherwise fall back to the last
  // closed round so pages reached without ?round still work.
  getDownloadRound = (sortedRounds) => {
    const urlRoundNumber = this.props.roundInUrl != null
      ? Number(this.props.roundInUrl)
      : null;
    const urlRound = (urlRoundNumber != null && !isNaN(urlRoundNumber))
      ? sortedRounds.find(r => Number(r.number) === urlRoundNumber)
      : null;
    return urlRound || this.getLastClosedRound(sortedRounds);
  };

  get currentDate() {
    const [year, month, day] = new Date().toISOString().split("T")[0].split("-");
    return `${month}-${day}-${year}`;
  }

  downloadPerformanceFile = () => {
    const { performanceDownloadFile } = this.state;
    if (!performanceDownloadFile) return;

    downloadRequest(performanceDownloadFile, "application/csv")
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `performance_${this.currentDate}.csv`;
        link.click();
        this.setState({ performanceDownloading: false });
      })
      .catch(err => {
        console.error(err);
        this.setState({ performanceDownloading: false });
        alert(DOWNLOAD_ERROR);
      });
  };

  // Three-step pattern matched from the instructor dashboard's
  // downloadPerformanceData: kick scores via /usergrade/run/, then
  // enqueue the CSV job via /reports/performance-csv-add/, then poll
  // the returned task URL until it reports Completed and pull the
  // blob through downloadRequest.
  downloadPerformanceData = (e) => {
    if (e) e.preventDefault();

    const {
      performanceDownloadRequest,
      performanceDownloadCount,
      performanceDownloadFile,
      latestClosedSurvey,
    } = this.state;

    if (performanceDownloadFile) {
      this.downloadPerformanceFile();
      return;
    }

    const sortedRounds = (this.props.rounds || [])
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number));
    const downloadRound = this.getDownloadRound(sortedRounds);
    if (!downloadRound || downloadRound.is_finished !== true) {
      alert("Performance data is available once the selected round has closed.");
      return;
    }

    const game = this.props.game;
    const gameId = game ? game.id : Auth.getGameId();
    const surveyId = latestClosedSurvey ? latestClosedSurvey.id : null;
    const roundId = downloadRound.id;

    if (!performanceDownloadRequest) {
      this.setState({ performanceDownloading: true });
      postRequest(`${process.env.REACT_APP_API_URL}v2/games/usergrade/run/`)
        .send({
          game: gameId,
          ...(surveyId ? { survey: surveyId } : {}),
        })
        .type("form")
        .then(res => {
          if (res.statusCode === 200 || res.statusCode === 201) {
            postRequest(`${process.env.REACT_APP_API_URL}v2/reports/performance-csv-add/`)
              .send({
                round: roundId,
                ...(surveyId ? { survey: surveyId } : {}),
              })
              .type("form")
              .then(resp => {
                const { download_file } = resp.body;
                // The dashboard strips the leading "/api/" prefix
                // before appending to REACT_APP_API_URL.
                const nextDownloadRequest = download_file
                  ? download_file.substring(5)
                  : null;
                this.setState(
                  {
                    performanceDownloadRequest: nextDownloadRequest,
                    performanceDownloadCount: 0,
                    performanceDownloadFile: null,
                  },
                  () => this.downloadPerformanceData()
                );
              })
              .catch(err => {
                console.error(err);
                this.setState({ performanceDownloading: false });
                alert(DOWNLOAD_ERROR);
              });
          } else {
            this.setState({ performanceDownloading: false });
            alert(DOWNLOAD_ERROR);
          }
        })
        .catch(err => {
          console.error(err);
          this.setState({ performanceDownloading: false });
        });
      return;
    }

    if (performanceDownloadCount === DOWNLOAD_MAX_ATTEMPTS) {
      alert(DOWNLOAD_ERROR);
      this.setState({ performanceDownloadCount: 0, performanceDownloading: false });
      return;
    }

    if (this._pollingPerformance) return;
    this._pollingPerformance = true;

    getRequest(`${process.env.REACT_APP_API_URL}${performanceDownloadRequest}`)
      .then(res => {
        this._pollingPerformance = false;
        const { status, download_link } = res.body;

        this.setState({
          performanceDownloadCount: performanceDownloadCount + 1,
        });

        if (status !== "Completed") {
          setTimeout(this.downloadPerformanceData, DOWNLOAD_ATTEMPT_DELAY);
        } else {
          this.setState(
            {
              performanceDownloadFile: download_link,
              performanceDownloadCount: 0,
            },
            () => this.downloadPerformanceFile()
          );
        }
      })
      .catch(err => {
        this._pollingPerformance = false;
        console.error(err);
        this.setState({ performanceDownloading: false });
        alert(DOWNLOAD_ERROR);
      });
  };

  render() {
    const teamplayers = this.props.teamplayers || [];
    const teams = this.props.teams || [];
    const {
      loaded,
      weights,
      roundsScores,
      peerScores,
      individualScores,
      individualScoresProcessing,
      latestClosedSurvey,
    } = this.state;

    if (!loaded) {
      return (
        <Wrapper>
          <PageTitle>Performance Scores</PageTitle>
          <Loader type="ball-scale-ripple" active />
        </Wrapper>
      );
    }

    const sortedRounds = (this.props.rounds || [])
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number));
    // Show up to 3 round columns to match the spec; if a game has
    // fewer rounds we render only those.
    const visibleRounds = sortedRounds.slice(0, 3);
    const lastClosedRound = this.getLastClosedRound(sortedRounds);

    const teamsById = {};
    teams.forEach(t => { teamsById[String(t.id)] = t.name; });

    // Build display rows, joining teamplayers with the per-round
    // team grading map and the per-user peer score map.
    const rows = teamplayers.map(tp => {
      const teamId = tp.team;
      const teamName = teamsById[String(teamId)] || "—";
      const first = (tp.user && tp.user.first_name) || "";
      const last = (tp.user && tp.user.last_name) || "";
      const fullName = `${first} ${last}`.trim() || (tp.user && tp.user.username) || "—";

      const roundValues = visibleRounds.map(r => {
        // Only surface a round's current_weighted_value once the round
        // has closed. For live rounds the BE's teamgradings/overall
        // endpoint returns a provisional value (often a carry-over
        // from the prior round) that misleads instructors into
        // thinking the live round has already been graded. Render
        // "—" instead until is_finished flips to true.
        if (r.is_finished !== true) return null;
        const cell = roundsScores[r.id] && roundsScores[r.id][String(teamId)];
        return cell && !isNaN(cell.current) ? cell.current : null;
      });

      const teamScore = (lastClosedRound
        && roundsScores[lastClosedRound.id]
        && roundsScores[lastClosedRound.id][String(teamId)]
        && !isNaN(roundsScores[lastClosedRound.id][String(teamId)].cumulative))
        ? roundsScores[lastClosedRound.id][String(teamId)].cumulative
        : null;

      const userId = tp.user && tp.user.id;
      const peer = peerScores[String(userId)] || null;
      const peerScore = peer && peer.peer_score != null && !isNaN(Number(peer.peer_score))
        ? Number(peer.peer_score)
        : null;
      const completion = peer ? !!peer.completion : null;

      // Pulled from /usergrade/course/<course_id>/ rather than
      // computed client-side — keeps the formula authoritative on
      // the backend (curve, adjustments, etc. are all included).
      const indivRaw = individualScores ? individualScores[String(userId)] : undefined;
      const individualScore = (indivRaw != null && !isNaN(Number(indivRaw)))
        ? Number(indivRaw)
        : null;

      return {
        id: tp.id,
        fullName,
        teamName,
        roundValues,
        teamScore,
        peerScore,
        completion,
        individualScore,
      };
    });

    rows.sort((a, b) => {
      const t = a.teamName.localeCompare(b.teamName);
      if (t !== 0) return t;
      return a.fullName.localeCompare(b.fullName);
    });

    // Disable Download when the targeted round (sidebar's ?round=N
    // or fallback to last closed) hasn't closed yet, so the click
    // never produces the "selected round has closed" alert.
    const downloadRound = this.getDownloadRound(sortedRounds);
    const downloadDisabled = !downloadRound
      || downloadRound.is_finished !== true
      || this.state.performanceDownloading;

    return (
      <Wrapper>
        <TitleRow>
          <PageTitle>Performance Scores</PageTitle>
          <DownloadLink
            href="#"
            disabled={downloadDisabled}
            onClick={this.downloadPerformanceData}
          >
            {this.state.performanceDownloading ? "Preparing download…" : "Download"}
          </DownloadLink>
        </TitleRow>
        <PageSubtitle>
          View the latest team, peer, and individual scores. Each round's team score rolls up into a weighted cumulative score, which then combines with the peer score to produce the individual score. All scores adjust until every round and survey closes.
        </PageSubtitle>
        {individualScoresProcessing && (
          <PageSubtitle>
            Individual scores are being calculated. This usually takes 5–10 minutes — check back shortly.
          </PageSubtitle>
        )}

        <TableWrapper>
          <Table>
            <thead>
              <HeadGroupRow>
                <HeadCell>Name</HeadCell>
                <HeadCell center>Team</HeadCell>
                {visibleRounds.map(r => (
                  <HeadCell center key={`hr-${r.id}`}>Round {r.number}</HeadCell>
                ))}
                <HeadCell center>Team Score</HeadCell>
                <HeadCell center>Peer Score</HeadCell>
                <HeadCell center>Final Eval Submitted?</HeadCell>
                <HeadCell center>Individual Score</HeadCell>
              </HeadGroupRow>
              <SubHeadRow>
                <SubHeadCell />
                <SubHeadCell center />
                {visibleRounds.map(r => (
                  <SubHeadCell center key={`sw-${r.id}`}>
                    Weight: {formatWeightPct(r.grading_weight)}
                  </SubHeadCell>
                ))}
                <SubHeadCell center>Weight: {formatWeightPct(weights.team_grade)}</SubHeadCell>
                <SubHeadCell center>Weight: {formatWeightPct(weights.peer_weight)}</SubHeadCell>
                <SubHeadCell center />
                <SubHeadCell center />
              </SubHeadRow>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <EmptyRow>
                  <EmptyCell colSpan={6 + visibleRounds.length}>
                    No students found.
                  </EmptyCell>
                </EmptyRow>
              )}
              {rows.map(row => (
                <Row key={row.id}>
                  <Cell>{row.fullName}</Cell>
                  <Cell center>{row.teamName}</Cell>
                  {row.roundValues.map((v, i) => (
                    <Cell center key={`rv-${row.id}-${i}`}>{formatPct(v)}</Cell>
                  ))}
                  <Cell center>{formatPct(row.teamScore)}</Cell>
                  <Cell center>{formatPct(row.peerScore)}</Cell>
                  <Cell center>
                    {row.completion === null ? (
                      <Pill tone="na">—</Pill>
                    ) : row.completion ? (
                      <Pill tone="yes">Y</Pill>
                    ) : (
                      <Pill tone="no">N</Pill>
                    )}
                  </Cell>
                  <Cell center>{formatPct(row.individualScore)}</Cell>
                </Row>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    );
  }
}

export default FinalScores;



// WEBPACK FOOTER //
// ./src/instructor/containers/final-scores.js